/**
 * Tailtrums Dog Food Quiz — dog-food-quiz.js
 * A self-contained, dependency-free step controller.
 * Each <section data-quiz> on the page gets its own independent instance,
 * so multiple quizzes can exist on one store without colliding.
 */
(function () {
  'use strict';

  class DogFoodQuiz {
    constructor(root) {
      this.root = root;
      this.steps = Array.from(root.querySelectorAll('[data-quiz-step]'));
      this.backBtn = root.querySelector('[data-quiz-back]');
      this.progressFill = root.querySelector('[data-quiz-progress-fill]');
      this.progressPaw = root.querySelector('[data-quiz-progress-paw]');
      this.progressWrap = root.querySelector('[data-quiz-progress]');

      this.history = [0];
      this.answers = {};

      if (!this.steps.length) return;

      this.bindStepEvents();
      this.goTo(0, { animate: false });

      if (this.backBtn) {
        this.backBtn.addEventListener('click', () => this.goBack());
      }
    }

    currentIndex() {
      return this.history[this.history.length - 1];
    }

    goTo(index, opts) {
      opts = opts || {};
      const prevIndex = this.steps.findIndex((s) => s.classList.contains('is-active'));

      if (prevIndex > -1 && prevIndex !== index) {
        this.steps[prevIndex].classList.remove('is-active');
      }

      const step = this.steps[index];
      if (!step) return;
      step.classList.add('is-active');
      this.applyDogName(step);
      this.focusFirstField(step);
      this.updateProgress(index);

      if (this.backBtn) {
        this.backBtn.hidden = this.history.length <= 1;
      }

      // Auto-validate continue button state for the newly shown step
      this.refreshContinueState(step);

      // Loading steps auto-advance after a delay and animate a checklist
      if (step.dataset.stepType === 'loading') {
        this.runLoadingStep(step);
      }
    }

    updateProgress(index) {
      if (!this.progressFill) return;
      const pct = this.steps.length <= 1 ? 100 : Math.round((index / (this.steps.length - 1)) * 100);
      this.progressFill.style.width = pct + '%';
      if (this.progressPaw) this.progressPaw.style.left = pct + '%';
      if (this.progressWrap) this.progressWrap.setAttribute('aria-valuenow', String(pct));
    }

    focusFirstField(step) {
      const field = step.querySelector('[data-quiz-field]');
      if (field && window.matchMedia('(min-width: 768px)').matches) {
        field.focus({ preventScroll: true });
      }
    }

    applyDogName(step) {
      const name = this.answers.dog_name;
      step.querySelectorAll('[data-quiz-dogname]').forEach((el) => {
        el.textContent = name && name.trim() ? name.trim() : 'your dog';
      });
    }

    advance() {
      const idx = this.currentIndex();
      const next = idx + 1;
      if (next >= this.steps.length) {
        return;
      }
      this.history.push(next);
      this.goTo(next);
    }

    goBack() {
      if (this.history.length <= 1) return;
      this.history.pop();
      this.goTo(this.currentIndex());
    }

    skipTo() {
      this.advance();
    }

    refreshContinueState(step) {
      const nextBtn = step.querySelector('[data-quiz-next]');
      if (!nextBtn) return;
      const type = step.dataset.stepType;

      if (type === 'text') {
        const required = step.dataset.required === 'true';
        const input = step.querySelector('[data-quiz-field]');
        const value = (input && input.value) || '';
        nextBtn.disabled = required && value.trim() === '';
      }

      if (type === 'choice') {
        const multi = step.dataset.multi === 'true';
        if (!multi) {
          const selected = step.querySelector('.dfq__choice-card.is-selected');
          nextBtn.disabled = !selected;
        } else {
          nextBtn.disabled = false;
        }
      }
    }

    bindStepEvents() {
      this.steps.forEach((step) => {
        const type = step.dataset.stepType;
        const fieldKey = step.dataset.field;

        // Generic "Continue" buttons
        step.querySelectorAll('[data-quiz-next]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.collectStepValue(step);
            this.advance();
          });
        });

        step.querySelectorAll('[data-quiz-skip]').forEach((btn) => {
          btn.addEventListener('click', () => this.skipTo());
        });

        if (type === 'text') {
          const input = step.querySelector('[data-quiz-field]');
          if (input) {
            input.addEventListener('input', () => this.refreshContinueState(step));
            input.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const btn = step.querySelector('[data-quiz-next]');
                if (btn && !btn.disabled) btn.click();
              }
            });
          }
        }

        if (type === 'choice') {
          const multi = step.dataset.multi === 'true';
          step.querySelectorAll('[data-quiz-choice]').forEach((card) => {
            card.addEventListener('click', () => {
              if (multi) {
                card.classList.toggle('is-selected');
              } else {
                step.querySelectorAll('[data-quiz-choice]').forEach((c) => c.classList.remove('is-selected'));
                card.classList.add('is-selected');
              }
              this.refreshContinueState(step);

              if (!multi) {
                this.collectStepValue(step);
                setTimeout(() => this.advance(), 220);
              }
            });
          });
        }

        if (type === 'number') {
          const input = step.querySelector('[data-quiz-field]');
          const decBtn = step.querySelector('[data-quiz-decrement]');
          const incBtn = step.querySelector('[data-quiz-increment]');
          const step_ = parseFloat(input.step) || 1;
          const min = input.min !== '' ? parseFloat(input.min) : -Infinity;
          const max = input.max !== '' ? parseFloat(input.max) : Infinity;

          const clamp = (v) => Math.min(max, Math.max(min, v));

          if (decBtn) {
            decBtn.addEventListener('click', () => {
              input.value = clamp((parseFloat(input.value) || 0) - step_);
            });
          }
          if (incBtn) {
            incBtn.addEventListener('click', () => {
              input.value = clamp((parseFloat(input.value) || 0) + step_);
            });
          }
        }

        if (type === 'tags') {
          step.querySelectorAll('[data-quiz-tag]').forEach((pill) => {
            pill.addEventListener('click', () => pill.classList.toggle('is-selected'));
          });
        }

        if (type === 'photo') {
          const dropZone = step.querySelector('[data-quiz-photo-drop]');
          const fileInput = step.querySelector('[data-quiz-photo-input]');
          const placeholder = step.querySelector('[data-quiz-photo-placeholder]');
          const preview = step.querySelector('[data-quiz-photo-preview]');

          if (fileInput) {
            fileInput.addEventListener('change', () => {
              const file = fileInput.files && fileInput.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (e) => {
                preview.src = e.target.result;
                preview.hidden = false;
                placeholder.hidden = true;
              };
              reader.readAsDataURL(file);
            });
          }
        }
      });
    }

    collectStepValue(step) {
      const type = step.dataset.stepType;
      const key = step.dataset.field;
      if (!key) return;

      if (type === 'text' || type === 'number') {
        const input = step.querySelector('[data-quiz-field]');
        this.answers[key] = input ? input.value : '';
      }

      if (type === 'choice') {
        const multi = step.dataset.multi === 'true';
        const selected = Array.from(step.querySelectorAll('.dfq__choice-card.is-selected')).map((c) => c.dataset.value);
        this.answers[key] = multi ? selected : (selected[0] || '');
      }

      if (type === 'tags') {
        const selected = Array.from(step.querySelectorAll('.dfq__tag-pill.is-selected')).map((p) => p.dataset.value);
        this.answers[key] = selected;
      }

      this.syncPlanStep();
    }

    runLoadingStep(step) {
      const items = Array.from(step.querySelectorAll('.dfq__loading-item'));
      const duration = parseInt(step.dataset.duration, 10) || 2500;
      const perItem = items.length ? duration / items.length : duration;

      items.forEach((item, i) => {
        setTimeout(() => item.classList.add('is-done'), perItem * (i + 1));
      });

      clearTimeout(this._loadingTimer);
      this._loadingTimer = setTimeout(() => this.advance(), duration + 250);
    }

    syncPlanStep() {
      const planStep = this.steps.find((s) => s.dataset.stepType === 'plan');
      if (!planStep) return;

      const propMap = {
        dog_name: 'dog_name',
        breed: 'breed',
        sex: 'sex',
        age: 'age',
        weight: 'weight',
        body_shape: 'body_shape',
        activity: 'activity',
        allergies: 'allergies'
      };

      Object.keys(propMap).forEach((key) => {
        const input = planStep.querySelector('[data-quiz-property="' + propMap[key] + '"]');
        if (input) {
          const val = this.answers[key];
          input.value = Array.isArray(val) ? val.join(', ') : (val || '');
        }
      });

      const summaryList = planStep.querySelector('[data-quiz-summary-list]');
      if (summaryList) {
        const labels = {
          dog_name: 'Name',
          breed: 'Breed',
          sex: 'Sex',
          age: 'Age',
          weight: 'Weight',
          body_shape: 'Body shape',
          activity: 'Activity level',
          allergies: 'Allergies'
        };
        summaryList.innerHTML = '';
        Object.keys(labels).forEach((key) => {
          const val = this.answers[key];
          const display = Array.isArray(val) ? val.join(', ') : val;
          if (!display) return;
          const dt = document.createElement('dt');
          dt.textContent = labels[key];
          const dd = document.createElement('dd');
          dd.textContent = display;
          summaryList.appendChild(dt);
          summaryList.appendChild(dd);
        });
      }
    }
  }

  function initSellingPlanSync(root) {
    const form = root.querySelector('[data-quiz-form]');
    if (!form) return;

    const radios = root.querySelectorAll('[data-quiz-selling-plan]');
    const hiddenInput = form.querySelector('[data-quiz-selling-plan-input]');

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (hiddenInput) hiddenInput.value = radio.checked ? radio.value : hiddenInput.value;
      });
    });

    // Set initial value from whichever radio is checked by default
    const checked = root.querySelector('[data-quiz-selling-plan]:checked');
    if (checked && hiddenInput) hiddenInput.value = checked.value;

    form.addEventListener('submit', (e) => {
      const submitBtn = form.querySelector('[data-quiz-submit]');
      const label = submitBtn && submitBtn.querySelector('.dfq__btn-label');
      const spinner = submitBtn && submitBtn.querySelector('.dfq__btn-spinner');

      if (submitBtn) submitBtn.disabled = true;
      if (spinner) spinner.hidden = false;

      // Let the native form submission proceed to /cart/add (full page nav),
      // or hijack here with fetch() if the theme prefers an AJAX cart drawer:
      if (window.theme && typeof window.theme.addToCartAjax === 'function') {
        e.preventDefault();
        const formData = new FormData(form);
        window.theme.addToCartAjax(formData).finally(() => {
          if (submitBtn) submitBtn.disabled = false;
          if (spinner) spinner.hidden = true;
        });
      }
      // Otherwise, native submit proceeds normally.
    });
  }

  function init() {
    document.querySelectorAll('[data-quiz]').forEach((root) => {
      if (root.__dfqInitialized) return;
      root.__dfqInitialized = true;
      new DogFoodQuiz(root);
      initSellingPlanSync(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if the section is re-rendered by the Shopify theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-quiz]');
    if (root) {
      root.__dfqInitialized = false;
      init();
    }
  });
})();
